import { z } from "zod";

import { TagId, MessageId } from "@/types/branded";
import {
  gatekeeperPrecisionPayloadSchema,
  gatekeeperQuestionSchema,
} from "@/types/schemas/gatekeeper";
import { quotaLimitDetailsSchema } from "@/types/schemas/user-quota";

// Base tag schema - single definition
export const tagSchema = z.object({
  id: z.string().transform(TagId),
  label: z.string().min(1),
  color: z.string().regex(/^bg-.*$/),
});

// Agent message error details (from Dust API)
const messageErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  quotaExceeded: quotaLimitDetailsSchema.optional(),
});

export const gatekeeperPrecisionOutcomeSchema = z.enum(["rejected"]);

export const gatekeeperPrecisionPairSchema = z.object({
  clarification: z.string(),
  clarificationQuestions: z.array(gatekeeperQuestionSchema).optional(),
  userReply: z.string().optional(),
  structuredReply: gatekeeperPrecisionPayloadSchema.optional(),
  /** Score 0 attempt folded under the next question's precision workflow. */
  outcome: gatekeeperPrecisionOutcomeSchema.optional(),
  /** Fixed scope reminder below agent explanation (score 0). */
  rejectionNotice: z.string().optional(),
  /** Réponse envoyée, confirmation serveur en cours. */
  transmissionPending: z.boolean().optional(),
});

export const userIntentSchema = z.enum(["question", "precision"]);

export const gatekeeperQualificationKindSchema = z.enum([
  "clarification",
  "rejected",
]);

// Message with proper role discrimination
export const messageSchema = z.object({
  id: z.string().transform(MessageId),
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  timestamp: z.coerce.date(),
  status: z.enum(["created", "succeeded", "failed", "cancelled"]).optional(),
  error: messageErrorSchema.optional(),
  /** Duration of message generation in ms (from Dust API completionDurationMs). */
  completionDurationMs: z.number().optional(),
  /** User messages: question (default) vs gatekeeper precision reply. */
  userIntent: userIntentSchema.optional(),
  /** Assistant messages: gatekeeper qualification before SidoniSearch. */
  assistantOrigin: z.literal("gatekeeper").optional(),
  gatekeeperQualificationKind: gatekeeperQualificationKindSchema.optional(),
  /** Clarification history on the question bubble (complete + pending entries). */
  gatekeeperPrecisions: z.array(gatekeeperPrecisionPairSchema).optional(),
  /** Gatekeeper visual phase for the unified question + precisions block. */
  gatekeeperPhase: z.enum(["clarifying", "qualified"]).optional(),
});

// Conversation question schema
export const conversationQuestionSchema = z.object({
  id: z.string(), // user message ID
  content: z.string(), // question text
  timestamp: z.coerce.date(),
});

// AI Model schema
export const aiModelSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  avatarUrl: z.string().optional(),
  avatarColor: z.string().optional(),
});

// Sort order schema
export const sortOrderSchema = z.enum(["asc", "desc"]);

// Chain of thought action schema
export const chainOfThoughtActionSchema = z.object({
  id: z.string(),
  type: z.string(),
  content: z.string(),
  timestamp: z.coerce.date(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

// Message chain of thought schema
export const messageChainOfThoughtSchema = z.object({
  messageId: z.string(),
  messagePreview: z.string(),
  timestamp: z.coerce.date(),
  content: z.string(),
  actions: z.array(chainOfThoughtActionSchema),
  agentConfigurationId: z.string().optional(),
});

// In-progress message for stream resumption
const inProgressMessageSchema = z.object({
  sId: z.string(),
  snapshotContent: z.string(),
});
export type InProgressMessage = z.infer<typeof inProgressMessageSchema>;

// Extracted conversation data schema
export const extractedConversationDataSchema = z.object({
  messages: z.array(messageSchema),
  allChainOfThoughts: z.array(messageChainOfThoughtSchema),
  lastAgentConfigurationId: z.string().nullable(),
  inProgressMessage: inProgressMessageSchema.nullable(),
});

// Export data schema
export const exportDataSchema = z.object({
  tag: z.string(),
  exportDate: z.string(),
  conversationsCount: z.number(),
  conversations: z.array(
    z.object({
      title: z.string(),
      date: z.string(),
      documentTypes: z.array(z.string()),
      customTags: z.array(z.string()),
    })
  ),
});

// Source metadata schema (for citations)
export const sourceMetadataSchema = z.object({
  id: z.string(),
  title: z.string(),
  url: z.string().optional(),
  date: z.string().optional(),
  summary: z.string().optional(),
  sourceType: z.enum(["SIDONI", "Légifrance", "other"]).optional(),
});

// Inferred types
export type Tag = z.infer<typeof tagSchema>;
export type MessageError = z.infer<typeof messageErrorSchema>;
export type Message = z.infer<typeof messageSchema>;
export type GatekeeperPrecisionPair = z.infer<
  typeof gatekeeperPrecisionPairSchema
>;
export type UserIntent = z.infer<typeof userIntentSchema>;
export type GatekeeperQualificationKind = z.infer<
  typeof gatekeeperQualificationKindSchema
>;
export type ConversationQuestion = z.infer<typeof conversationQuestionSchema>;
export type AIModel = z.infer<typeof aiModelSchema>;
export type SortOrder = z.infer<typeof sortOrderSchema>;
export type ChainOfThoughtAction = z.infer<typeof chainOfThoughtActionSchema>;
export type MessageChainOfThought = z.infer<typeof messageChainOfThoughtSchema>;
export type ExtractedConversationData = z.infer<
  typeof extractedConversationDataSchema
>;
export type ExportData = z.infer<typeof exportDataSchema>;
export type SourceMetadata = z.infer<typeof sourceMetadataSchema>;
